import React, { useCallback } from 'react'
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import classes from './SplitButton.scss';

import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

export type SplitButtonMenuOption = {
  title: string,
  link?: string,
  onClick?(evt: React.MouseEvent): void
}

export type SplitButtonProps = {
  children: React.ReactElement,
  menuOptions: SplitButtonMenuOption[],
  className?: string
}


export default function SplitButton({ menuOptions, children, className }: SplitButtonProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback((event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  }, []);

  return menuOptions.length ? (
    <div className={classes.container}>
      <ButtonGroup
        disableElevation
        className={classNames(className, [classes.buttonGroup])}
        variant="contained"
        color="primary"
        ref={anchorRef}
      >
        {children}
        <Button
          color="primary"
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          classes={{
            root: classes.toggleButton
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        transition
        disablePortal={false}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        modifiers={[
          {
            name: 'flip',
            enabled: true,
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundariesElement: 'viewport',
            },
          },
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {menuOptions.map((option, index) => (
                    <MenuItem
                      key={index}
                      onClick={option.onClick}
                      component={option.link ? Link : undefined}
                      to={option.link}
                    >
                      {option.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  ) : <div className={className}>{children}</div>;
}
